import {
  CUSTOM_ROUTINE_TRIGGER,
  FLAG,
  INVALID_FEATURE,
  Mode,
  PackEditorTabMode
} from 'enums';
import {
  BREAK_FREQUENCY_MINUTES,
  ACTIVITY_TYPES,
  EMPTY_STRING,
  PLATFORMS,
  TAB,
  TIME_FORMAT_24H
} from 'constants/general';
import {
  ActivityType,
  FocusModeTemplateType
} from 'interfaces/common.interface';
import {
  CustomRoutineType,
  RoutineTimeValidation,
  SettingsType,
  ValidationError
} from 'interfaces/setting.interface';
import moment from 'moment';
import { t } from 'services/i18n.service';
import { normalizeHabitName } from 'utils/helpers/normalizeHabitName';
import { formatTimeTo12Hour } from 'utils/time.util';
import {
  hasInvalidCustomRoutineSchedules,
  isActivityCutoffTimeValid,
  isValidHabitName,
  isYoutubeURL,
  shouldValidateActivityCutoff,
  validateRoutineTimes
} from './';
import {
  isValidateFocusModeTemplate,
  shouldShowGlobalCutoffTime
} from 'utils/settings.util';
import type { SettingsContentMode } from 'utils/settings.util';
import {
  appendActivityHabitFieldValidationErrors,
  getActivityHabitFieldErrorKeys
} from 'utils/habit-edit-form.util';
import { buildHabitValidationNavigation } from 'utils/habit-validation.util';

type ActivityHabitValidationParams = {
  errors: ValidationError[];
  activity: ActivityType;
  habitPosition: number;
  tabTitle: string;
  activityType: string;
  customRoutinePosition?: number;
  durationFeature?: INVALID_FEATURE;
  completionFeature?: INVALID_FEATURE;
};

const appendActivityValidationErrors = ({
  errors,
  activity,
  habitPosition,
  tabTitle,
  activityType,
  customRoutinePosition,
  durationFeature,
  completionFeature
}: ActivityHabitValidationParams): void => {
  const navigation = buildHabitValidationNavigation({
    activity,
    activityType,
    habitPosition,
    customRoutinePosition
  });
  const listPosition =
    customRoutinePosition !== undefined ? customRoutinePosition : habitPosition;
  const normalizedName = normalizeHabitName(activity.name ?? EMPTY_STRING);

  if (normalizedName && !isValidHabitName(normalizedName)) {
    errors.push({
      tab: { title: tabTitle, habit: navigation.habitName },
      feature: INVALID_FEATURE.HABIT_NAME,
      message: t('validation.invalid_characters'),
      invalid_value: normalizedName,
      position: listPosition,
      ...navigation
    });
  }

  appendActivityHabitFieldValidationErrors(errors, activity, {
    tabTitle,
    habitLabel: navigation.habitName,
    position: listPosition,
    durationFeature,
    completionFeature,
    ...navigation
  });
};

const isActivityNonNameFieldsValidForSave = (activity: ActivityType): boolean => {
  const fieldErrors = getActivityHabitFieldErrorKeys(activity);
  return !fieldErrors.duration && !fieldErrors.completion;
};

const isOptionalYoutubeUrlValid = (url?: string | null) =>
  !url || isYoutubeURL(url);

/** True when HTML has visible text (matches pack description expectations). */
export const hasMeaningfulRichTextContent = (html?: string | null): boolean =>
  Boolean(html?.replace(/<[^>]*>/g, '').trim());

const isRequiredYoutubeUrlValid = (url?: string | null): boolean => {
  const trimmed = url?.trim() ?? '';
  return Boolean(trimmed) && isYoutubeURL(trimmed);
};

export const getValidationErrors = (
  settings: SettingsType | null,
  contentMode: SettingsContentMode,
  currentViewedFocusTemplate: FocusModeTemplateType | null,
  isTextEditorWithinCharacterLimit = true,
  isUserAdmin = false,
  flags: FLAG[] = [],
  platform: string = PLATFORMS.WEB
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const showGlobalCutoffTime = shouldShowGlobalCutoffTime(flags, platform);

  if (currentViewedFocusTemplate) {
    !currentViewedFocusTemplate?.name &&
      errors.push({
        tab: { title: TAB.TEMPLATE_INFO },
        feature: INVALID_FEATURE.PACK_NAME,
        message: t('validation.empty_content'),
        invalid_value: t('validation.empty_content')
      });

    !isOptionalYoutubeUrlValid(
      currentViewedFocusTemplate?.description_video_url
    ) &&
      errors.push({
        tab: { title: TAB.TEMPLATE_INFO },
        feature: INVALID_FEATURE.PACK_DESCRIPTION_URL,
        message: t('validation.invalid_url'),
        invalid_value: currentViewedFocusTemplate?.description_video_url
      });

    !isOptionalYoutubeUrlValid(currentViewedFocusTemplate?.welcome_video_url) &&
      errors.push({
        tab: { title: TAB.TEMPLATE_INFO },
        feature: INVALID_FEATURE.PACK_WELCOME_URL,
        message: t('validation.invalid_url'),
        invalid_value: currentViewedFocusTemplate?.welcome_video_url
      });

    !currentViewedFocusTemplate?.allowed_urls?.length &&
      errors.push({
        tab: { title: TAB.APPS_AND_URLS },
        feature: INVALID_FEATURE.ALLOWED_URLS,
        message: t('validation.invalid_url')
      });

    !currentViewedFocusTemplate?.allowed_apps?.length &&
      errors.push({
        tab: { title: TAB.APPS_AND_URLS },
        feature: INVALID_FEATURE.ALLOWED_APPS,
        message: t('validation.empty_allowed_apps')
      });

    if (!isTextEditorWithinCharacterLimit) {
      errors.push({
        tab: { title: TAB.TEMPLATE_INFO },
        feature: INVALID_FEATURE.TEXT_EDITOR_CHARACTER_LIMIT,
        message: t('validation.text_limit_reached')
      });
    }
  } else {
    if (settings) {
      const sections = [
        { key: ACTIVITY_TYPES.MORNING, tab: TAB.MORNING },
        { key: ACTIVITY_TYPES.BREAKING, tab: TAB.MICRO_BREAKS },
        { key: ACTIVITY_TYPES.EVENING, tab: TAB.EVENING }
      ];
      sections.forEach(({ key, tab }) => {
        type SectionKey = keyof Pick<
          SettingsType,
          'morning_activities' | 'break_activities' | 'evening_activities'
        >;
        const sectionKey = key as SectionKey;
        const activities = settings?.[sectionKey] ?? [];
        activities.forEach((activity: ActivityType, index: number) => {
          appendActivityValidationErrors({
            errors,
            activity,
            habitPosition: index,
            tabTitle: tab,
            activityType: key
          });
        });
      });

      const custom_routines = settings?.custom_routines ?? [];
      custom_routines.forEach(
        (routine: CustomRoutineType, routineIndex: number) => {
          const normalizedRoutineName = normalizeHabitName(
            routine.name ?? EMPTY_STRING
          );
          if (!normalizedRoutineName) {
            errors.push({
              tab: { title: TAB.CUSTOM_ROUTINES },
              feature: INVALID_FEATURE.HABIT_NAME,
              message: t('validation.empty_content'),
              invalid_value: `Custom Routine #${routineIndex + 1}`,
              position: routineIndex
            });
          } else if (!isValidHabitName(normalizedRoutineName)) {
            errors.push({
              tab: { title: TAB.CUSTOM_ROUTINES },
              feature: INVALID_FEATURE.HABIT_NAME,
              message: t('validation.invalid_characters'),
              invalid_value: normalizedRoutineName,
              position: routineIndex
            });
          }

          if (routine.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE) {
            if (!routine.start_time) {
              errors.push({
                tab: { title: TAB.CUSTOM_ROUTINES },
                feature: INVALID_FEATURE.START_UP,
                message: t('validation.invalid_time'),
                invalid_value: t('validation.empty_content'),
                position: routineIndex
              });
            }
            if (!routine.end_time) {
              errors.push({
                tab: { title: TAB.CUSTOM_ROUTINES },
                feature: INVALID_FEATURE.START_UP,
                message: t('validation.invalid_time'),
                invalid_value: t('validation.empty_content'),
                position: routineIndex
              });
            }
            if (routine.start_time && routine.end_time) {
              const isInvalidStartTime = moment(
                routine.start_time,
                TIME_FORMAT_24H
              ).isSameOrAfter(
                moment(routine.end_time, TIME_FORMAT_24H),
                'minute'
              );
              const isInvalidEndTime = moment(
                routine.end_time,
                TIME_FORMAT_24H
              ).isSameOrBefore(
                moment(routine.start_time, TIME_FORMAT_24H),
                'minute'
              );

              if (isInvalidStartTime || isInvalidEndTime) {
                errors.push({
                  tab: { title: TAB.CUSTOM_ROUTINES },
                  feature: INVALID_FEATURE.START_UP,
                  message: t('validation.invalid_time'),
                  invalid_value: `${routine.start_time} - ${routine.end_time}`,
                  position: routineIndex
                });
              }
            }

            if (!routine.days_of_week?.length) {
              errors.push({
                tab: { title: TAB.CUSTOM_ROUTINES },
                feature: INVALID_FEATURE.BREAK_FREQUENCY,
                message: t('validation.select_at_least_one_day_for_routine', {
                  routine: routine.name ?? ''
                }),
                invalid_value: t('validation.invalid_day_of_week'),
                position: routineIndex
              });
            }
          }

          const standalone_activities = routine.standalone_activities ?? [];
          standalone_activities.forEach(
            (activity: ActivityType, activityIndex: number) => {
              appendActivityValidationErrors({
                errors,
                activity,
                habitPosition: activityIndex,
                tabTitle: TAB.CUSTOM_ROUTINES,
                activityType: ACTIVITY_TYPES.CUSTOM_ROUTINES,
                customRoutinePosition: routineIndex,
                durationFeature: INVALID_FEATURE.BREAK_FREQUENCY,
                completionFeature: INVALID_FEATURE.HABIT_NAME
              });
            }
          );
        }
      );
    }

    const isErrorNotAdded = (feature: INVALID_FEATURE, position?: number) => {
      if (position !== undefined) {
        return errors.every(
          (error) => error.feature !== feature || error.position !== position
        );
      }
      return errors.every((error) => error.feature !== feature);
    };

    if (contentMode === Mode.DEFAULT) {
      if (
        (!settings?.break_after_minutes ||
          settings.break_after_minutes > BREAK_FREQUENCY_MINUTES.MAX) &&
        isErrorNotAdded(INVALID_FEATURE.BREAK_FREQUENCY)
      ) {
        errors.push({
          tab: { title: TAB.TIMING },
          message: t('validation.invalid_value'),
          feature: INVALID_FEATURE.BREAK_FREQUENCY,
          invalid_value: t('validation.empty_content')
        });
      }

      if (
        !settings?.startup_time &&
        isErrorNotAdded(INVALID_FEATURE.START_UP)
      ) {
        errors.push({
          tab: { title: TAB.TIMING },
          message: t('validation.invalid_time'),
          feature: INVALID_FEATURE.START_UP,
          invalid_value: t('validation.empty_content')
        });
      }

      if (
        !settings?.shutdown_time &&
        isErrorNotAdded(INVALID_FEATURE.SHUTDOWN)
      ) {
        errors.push({
          tab: { title: TAB.TIMING },
          message: t('validation.invalid_time'),
          feature: INVALID_FEATURE.SHUTDOWN,
          invalid_value: t('validation.empty_content')
        });
      }

      const routineTimeValidation = validateRoutineTimes(
        settings?.startup_time,
        settings?.shutdown_time,
        showGlobalCutoffTime
          ? settings?.cutoff_time_for_non_high_priority_activities
          : undefined
      );

      if (!routineTimeValidation.isValid.startupTime) {
        routineTimeValidation.errorMessages.startupTime.forEach((message) => {
          if (isErrorNotAdded(INVALID_FEATURE.START_UP)) {
            errors.push({
              tab: { title: TAB.TIMING },
              message,
              feature: INVALID_FEATURE.START_UP,
              invalid_value:
                settings?.startup_time ?? t('validation.empty_content')
            });
          }
        });
      }

      if (!routineTimeValidation.isValid.shutdownTime) {
        routineTimeValidation.errorMessages.shutdownTime.forEach((message) => {
          if (isErrorNotAdded(INVALID_FEATURE.SHUTDOWN)) {
            errors.push({
              tab: { title: TAB.TIMING },
              message,
              feature: INVALID_FEATURE.SHUTDOWN,
              invalid_value:
                settings?.shutdown_time ?? t('validation.empty_content')
            });
          }
        });
      }

      if (!routineTimeValidation.isValid.cutoffTime) {
        routineTimeValidation.errorMessages.cutoffTime.forEach((message) => {
          if (isErrorNotAdded(INVALID_FEATURE.SLEEP_TIME)) {
            const formattedCutoffTime = formatTimeTo12Hour(
              settings?.cutoff_time_for_non_high_priority_activities ??
                EMPTY_STRING
            );

            errors.push({
              tab: { title: TAB.TIMING },
              message,
              feature: INVALID_FEATURE.SLEEP_TIME,
              invalid_value:
                formattedCutoffTime ||
                settings?.cutoff_time_for_non_high_priority_activities ||
                t('validation.empty_content')
            });
          }
        });
      }
    } else if (
      [PackEditorTabMode.ROUTINE, PackEditorTabMode.STANDALONE].includes(
        contentMode as PackEditorTabMode
      )
    ) {
      if (!settings?.pack_name?.trim()) {
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.PACK_NAME,
          message: t('validation.empty_content'),
          invalid_value: t('validation.empty_content')
        });
      }

      if (isUserAdmin && !settings?.creator_name?.trim()) {
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.PACK_CREATOR_NAME,
          message: t('validation.empty_content'),
          invalid_value: t('validation.empty_content')
        });
      }

      if (!isRequiredYoutubeUrlValid(settings?.description_video_url)) {
        const url = settings?.description_video_url;
        const trimmed = url?.trim() ?? '';
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.PACK_DESCRIPTION_URL,
          message: trimmed
            ? t('validation.invalid_url')
            : t('validation.empty_content'),
          invalid_value: trimmed || t('validation.empty_content')
        });
      }

      if (!isOptionalYoutubeUrlValid(settings?.welcome_video_url)) {
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.PACK_WELCOME_URL,
          message: t('validation.invalid_url'),
          invalid_value: settings?.welcome_video_url
        });
      }

      if (!hasMeaningfulRichTextContent(settings?.description)) {
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.PACK_DESCRIPTION,
          message: t('validation.empty_content'),
          invalid_value: t('validation.empty_content')
        });
      }

      if (!isTextEditorWithinCharacterLimit) {
        errors.push({
          tab: { title: TAB.PACK_INFO },
          feature: INVALID_FEATURE.TEXT_EDITOR_CHARACTER_LIMIT,
          message: t('text_limit_reached')
        });
      }

      if (
        contentMode === PackEditorTabMode.STANDALONE &&
        !settings?.standalone_activities?.length
      ) {
        errors.push({
          tab: { title: TAB.ACTIVITY },
          feature: INVALID_FEATURE.STANDALONE_ACTIVITIES,
          message: t('validation.empty_content'),
          invalid_value: t('validation.empty_content')
        });
      }
    }
  }

  return errors;
};

export const validateSettingsData = (
  currentSettings: SettingsType,
  currentViewedFocusTemplate: FocusModeTemplateType | null,
  contentMode: SettingsContentMode,
  isTextEditorWithinCharacterLimit: boolean,
  routineTimeValidation: RoutineTimeValidation,
  isUserAdmin = false
) => {
  const {
    morning_activities = [],
    break_activities = [],
    evening_activities = [],
    standalone_activities = [],
    custom_routines,

    break_after_minutes,
    welcome_video_url,
    description_video_url,
    pack_name,
    creator_name,
    description
  } = currentSettings || {};

  const customRoutineActivities = (custom_routines ?? []).flatMap((routine) =>
    routine.standalone_activities ?? []
  );

  const allActivities = [
    ...morning_activities,
    ...break_activities,
    ...evening_activities,
    ...standalone_activities,
    ...customRoutineActivities
  ];

  const areRoutinesDataValid = allActivities.every(isActivityNonNameFieldsValidForSave);

  const hasInvalidActivityCutoff = evening_activities.some(
    (activity) =>
      shouldValidateActivityCutoff(activity) &&
      !isActivityCutoffTimeValid(activity.cutoff_time_for_doing_activity)
  );

  const areRoutineTimesValid = Object.values(
    routineTimeValidation.isValid
  ).every(Boolean);
  const areCustomRoutineSchedulesInValid =
    hasInvalidCustomRoutineSchedules(custom_routines);

  const isBreakAfterMinutesValid =
    typeof break_after_minutes === 'number' &&
    break_after_minutes >= BREAK_FREQUENCY_MINUTES.MIN &&
    break_after_minutes <= BREAK_FREQUENCY_MINUTES.MAX;

  let isDataValid = false;

  if (
    [PackEditorTabMode.ROUTINE, PackEditorTabMode.STANDALONE].includes(
      contentMode as PackEditorTabMode
    )
  ) {
    const isWelcomeVideoUrlValid = isOptionalYoutubeUrlValid(welcome_video_url);

    const trimmedDescUrl = description_video_url?.trim() ?? '';
    const isDescriptionVideoUrlValid =
      Boolean(trimmedDescUrl) && isYoutubeURL(trimmedDescUrl);

    const isCreatorNameValid = !isUserAdmin || Boolean(creator_name?.trim());

    isDataValid =
      areRoutinesDataValid &&
      !hasInvalidActivityCutoff &&
      isWelcomeVideoUrlValid &&
      isDescriptionVideoUrlValid &&
      isCreatorNameValid &&
      Boolean(
        isTextEditorWithinCharacterLimit &&
        pack_name?.trim() &&
        hasMeaningfulRichTextContent(description)
      );

    if (contentMode === PackEditorTabMode.STANDALONE) {
      isDataValid = isDataValid && standalone_activities.length > 0;
    }
  } else if (contentMode === PackEditorTabMode.FOCUS_MODE) {
    isDataValid = isValidateFocusModeTemplate(
      currentViewedFocusTemplate,
      isTextEditorWithinCharacterLimit
    );
  } else {
    isDataValid =
      isBreakAfterMinutesValid &&
      areRoutinesDataValid &&
      !hasInvalidActivityCutoff &&
      areRoutineTimesValid &&
      !areCustomRoutineSchedulesInValid;
  }

  return isDataValid;
};
